import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

import { buildYetiScoresForKpis, UseUsersDictionary, UseUsersFullData, windowUsesRangeYeti, yetiBucketForWindow } from "../../../hooks/company";
import { filterKpisForPublicDisplay, filterLiveKpis, isKpiScorekeeperOnly } from "../../../constants/kpi";
import { isExcludedFromBoards } from "../../../hooks/kpi";
import { companyMonthStr, companyToday } from "../../../helpers/companyTime";
import { effectiveMetricColumns, hasMetricPlan, poolColumn, sourceWindowOf } from "../../../helpers/metricPlan";
import { applyClosedMonthRecompute, lastMonthKey, recomputeKpiCellPts } from "../../../helpers/historicMonthBoard";
import { recomputeEntryPts, recomputeScopedPlayerScores } from "../../../helpers/kpiPoints";
import { fetchWorkspaceScores, monthsForWindow, monthsForMetricPlan, resolveWindow, sumScores } from
  "../../../pages/CompanyMetrics/dashboards/useWorkspaceData";
import { effectiveMetricAmt, isBidAccuracyKpi, formatKpiPercent, metaByIdFromDefs, metricCellScored } from "../../../helpers/scoreAggregate";
import { displayTargetOf, kindOfKpi } from "../../../helpers/kpiTargetScale";
import { needsWindowRates, boundsForWindow, fetchWindowRates, applyWindowRatesToPerUser } from "../../../helpers/windowRates";

/**
 * Rows and columns for the leaderboard table.
 *
 * The component NEVER owns a metric list. A workspace decides what it tracks,
 * so the columns are read from whichever workspaces this card points at, every
 * time it renders. Change the workspace and the columns change with it.
 *
 * With more than one workspace the columns are the metrics they have IN COMMON,
 * matched on name: Technicians have no Close rate, so a combined board that
 * carried one would be a column of blanks for half the room.
 */

/* Metric ids are per-workspace, so a shared column is matched on its name. */
const nameKey = (k) => (k?.title || k?.name || "").trim().toLowerCase();

/** Closed Rate column key — used as the default tie-breaker. */
export const closedRateKeyOf = (columns) => {
  const hit = (columns || []).find((c) =>
    /\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b/i.test(String(c.label || c.key || "")),
  );
  return hit ? hit.key : null;
};

/**
 * Align % player amounts (often 0–1) with % targets (often 0–100).
 */
const alignPctPair = (playerAmt, targetAmt) => {
  let p = Number(playerAmt) || 0;
  let y = Number(targetAmt) || 0;
  if (Math.abs(p) <= 1 && Math.abs(y) > 1) p *= 100;
  else if (Math.abs(y) <= 1 && Math.abs(p) > 1) y *= 100;
  return { p, y };
};

/**
 * Does this player beat one metric's benchmark for the qualify gate?
 *
 * Amount vs Scorekeeper / Yeti bar is the rule — same "of $1,250" the custom
 * widgets print. Points-first used to mark AVG Ticket green at $850 when the
 * Yeti had $0 pts but a $1,250 amount bar.
 */
const beatsYetiMetric = (playerPts, yetiPts, playerAmt, yetiAmt, kind, scored = true) => {
  if (!scored) return false;
  let p = Number(playerAmt) || 0;
  let y = Number(yetiAmt) || 0;
  if (kind === "%") {
    ({ p, y } = alignPctPair(p, y));
  }
  /* Prefer the amount bar when one exists (Yeti pace or Scorekeeper target). */
  if (y > 0) {
    if (kind !== "%" && p <= 0) return false;
    return p >= y;
  }
  const pp = Number(playerPts) || 0;
  const yp = Number(yetiPts) || 0;
  if (pp > 0 || yp > 0) return pp >= yp;
  if (kind !== "%" && p <= 0) return false;
  return p >= y;
};

/** Pool points the same way amounts are pooled across workspaces. */
const poolPts = (cells, mode) => {
  const live = (cells || []).filter((c) => {
    if (!c || !c.present) return false;
    if (mode === "sum") return true;
    return (Number(c.amt) || 0) > 0 || (Number(c.pts) || 0) > 0;
  });
  if (!live.length) return 0;
  const sum = live.reduce((a, c) => a + (Number(c.pts) || 0), 0);
  return mode === "sum" ? sum : sum / live.length;
};

/**
 * The metrics a workspace puts on a board — the LIVE ones, capped, exactly the
 * set the workspace dashboard already shows with the LIVE badge.
 *
 * Reading every KPI a workspace owns put eleven columns on the card, most of
 * them all-zero metrics nobody games. `filterLiveKpis` is the same helper the
 * Team Performance card uses, so the leaderboard's columns and the dashboard's
 * tiles can never disagree about what this workspace plays for.
 *
 * Scorekeeper-only metrics and rollups (Total Revenue, Team Hours) are also
 * dropped here — same split TVLeaderboard makes: those points still count
 * toward totals/points elsewhere, but the metric itself has no column/tile
 * on any board or dashboard, only in Scorekeeper. Both exclusions run BEFORE
 * the 5-slot cap, not after — cutting a scorekeeper-only metric from an
 * already-capped list of 5 just leaves a hole instead of letting the
 * next-ranked live metric backfill the slot.
 */
const liveKpisOf = (defs) =>
  filterLiveKpis(
    filterKpisForPublicDisplay((defs || []).filter((k) => k && !k.isDraft && !k.disabled && nameKey(k) && !isExcludedFromBoards(k))),
  );

// Navbar periodIdx → bucket. Indexes match Homepage PERIOD_OPTIONS
// (0=yesterday, 1=today, 2=week, 3=month, 4=lastWeek, 5=lastMonth).
const PERIOD_KEY = {
  0: "yesterday",
  1: "today",
  2: "week",
  3: "month",
  4: "lastWeek",
  5: "lastMonth",
};

export const periodBucket = (periodIdx) => PERIOD_KEY[periodIdx] || "month";

/** Prefer an explicit board window string (Analytics / Homepage filter) over periodIdx. */
export const bucketFromWindow = (boardWindow, periodIdx = 3) => {
  if (!boardWindow || boardWindow === "follow") return periodBucket(periodIdx);
  if (boardWindow === "year") return "ytd";
  if (["today", "yesterday", "week", "lastWeek", "lastMonth", "month", "quarter", "ytd", "custom"].includes(boardWindow)) {
    return boardWindow;
  }
  return periodBucket(periodIdx);
};

/* The window BEFORE the one on screen — Movement compares the two, so it has
   to be a real previous period, never a guess. */
const previousWindow = (bucket) => {
  if (bucket === "ytd" || bucket === "year") {
    const start = companyToday().startOf("year").subtract(1, "year");
    return { from: start.clone(), to: start.clone().endOf("year") };
  }
  if (bucket === "quarter") {
    const start = companyToday().startOf("quarter").subtract(1, "quarter");
    return { from: start.clone(), to: start.clone().endOf("quarter") };
  }
  if (bucket === "lastWeek") {
    const start = companyToday().subtract(2, "week").startOf("week");
    return { from: start.clone(), to: start.clone().endOf("week") };
  }
  if (bucket === "lastMonth") {
    const start = companyToday().subtract(2, "month").startOf("month");
    return { from: start.clone(), to: start.clone().endOf("month") };
  }
  if (bucket === "week") {
    const start = companyToday().subtract(1, "week").startOf("week");
    return { from: start.clone(), to: start.clone().endOf("week") };
  }
  if (bucket === "custom") {
    return { from: null, to: null };
  }
  const unit = bucket === "today" || bucket === "yesterday" ? "day" : bucket;
  const start = companyToday().startOf(unit).subtract(1, unit);
  return { from: start.clone(), to: start.clone().endOf(unit) };
};

/* Points per player inside a closed window (sumScores is open-ended). */
const pointsBetween = (scores, from, to) => {
  const perUser = {};
  (scores || []).forEach((score) => {
    const ownerId = score.ownerId || score.userId || null;
    if (!ownerId) return;
    (score.days || []).forEach((day) => {
      const d = moment(day.day);
      if (d < from || d > to) return;
      Object.entries(day.kpis || {}).forEach(([kpiId, v]) => {
        if (kpiId === "total" || !v) return;
        perUser[ownerId] = (perUser[ownerId] || 0) + (Number(v.pts) || 0);
      });
    });
  });
  return perUser;
};

/** How a metric's amount is written — "$1,420", "58%", "63".
 *  Rate KPIs store 0–1 fractions (0.44 = 44%). Money wrongly tagged as % used
 *  to print "42845%" — if the number is huge for a rate, treat it as dollars. */
export const formatAmount = (kpi, value) => {
  const n = Number(value) || 0;
  let kind = kpi?.kind;
  if (kind !== "$" && kind !== "%") {
    const t = `${kpi?.title || kpi?.name || kpi?.label || ""}`.toLowerCase();
    if (/\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b/.test(t)) kind = "%";
  }
  if (kind === "%" && Math.abs(n) > 1000) kind = "$";
  if (kind === "$") return `$${Math.round(n).toLocaleString()}`;
  if (kind === "%") {
    /* 0–1 fraction → percent points; values already in 0–100 stay as-is. */
    const pct = Math.abs(n) <= 1 ? n : n / 100;
    return formatKpiPercent(kpi, pct);
  }
  return Math.round(n).toLocaleString();
};

/**
 * @param {string[]} workspaceIds  which workspaces feed this board
 * @param {string}   bucket        today | yesterday | week | lastWeek | month | …
 * @param {object}   plan          optional metric plan (see helpers/metricPlan)
 * @param {object}   [custom]      { from, to } when bucket is "custom"
 */
export default function useLeaderboardData(workspaceIds, bucket = "month", plan = null, custom = null) {
  const companyId = useSelector((s) => s.authReducer.userDetails?.companyId);
  const companyKpis = useSelector((s) => s.kpiReducer.companyKpis);
  const historicRows = useSelector((s) =>
    bucket === "lastMonth" ? s.kpiReducer.historicKpis?.[lastMonthKey()] : null,
  );
  const holidays = useSelector((s) => s.companyReducer.holidays);
  const usersDict = UseUsersDictionary();
  const allUsers = UseUsersFullData();

  const ids = useMemo(
    () => (workspaceIds || []).filter(Boolean).map(String),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(workspaceIds || []).filter(Boolean).map(String).join("|")],
  );

  const customKey = custom ? `${custom.from || ""}~${custom.to || ""}` : "";
  const [scoresByWs, setScoresByWs] = useState({});
  const [loadedFetchKey, setLoadedFetchKey] = useState("");
  /* Week/month/today share the current month on the wire — only cold-fetch
     should blank the table into skeletons. */
  const monthsKey = (() => {
    const boardWin = bucket === "year" ? "ytd" : bucket;
    if (hasMetricPlan(plan)) return monthsForMetricPlan(plan, boardWin, custom).join(",");
    return monthsForWindow(boardWin, custom).join(",");
  })();
  const fetchKey = `${companyId || ""}|${ids.join(",")}|${monthsKey}`;
  const hasScores = Object.keys(scoresByWs).length > 0;
  const fetching = !!ids.length && loadedFetchKey !== fetchKey;
  const loading = fetching && !hasScores;
  const refreshing = fetching && hasScores;

  useEffect(() => {
    if (!companyId || !ids.length) {
      setScoresByWs({});
      setLoadedFetchKey(fetchKey);
      return undefined;
    }
    let alive = true;
    const months = hasMetricPlan(plan)
      ? monthsForMetricPlan(plan, bucket === "year" ? "ytd" : bucket, custom)
      : monthsForWindow(bucket, custom);
    Promise.all(
      ids.map((id) =>
        fetchWorkspaceScores(companyId, id, months)
          .then((s) => [id, s])
          .catch(() => [id, []]),
      ),
    ).then((pairs) => {
      if (!alive) return;
      setScoresByWs(Object.fromEntries(pairs));
      setLoadedFetchKey(fetchKey);
    });
    return () => {
      alive = false;
    };
  }, [companyId, ids, bucket, customKey, fetchKey]);

  /* Closed Rate + Bid Accuracy + AVG Ticket for non-month windows (Today /
     Week / custom / …). sumScores' own day-fold has nothing to sum for these
     — the score is a single snapshot pinned to the 1st of the month — so
     without this the "[Player] KPIs" leaderboard card read 0%/$0 for every
     window except This Month/Last Month (same fix already wired into
     useWorkspaceData.js and useScopedMetrics.js for the other board cards). */
  const boardWinForRates = bucket === "year" ? "ytd" : bucket;
  const ratesKey = `${companyId || ""}|${ids.join(",")}|${boardWinForRates}|${customKey}|${JSON.stringify(plan)}`;
  const [windowRatesByWs, setWindowRatesByWs] = useState({});

  useEffect(() => {
    if (!companyId || !ids.length) {
      setWindowRatesByWs({});
      return undefined;
    }
    const needed = new Map();
    const addWin = (wsId, w, cust) => {
      if (!needsWindowRates(w)) return;
      const { from, to } = boundsForWindow(w, cust);
      if (!from || !to) return;
      needed.set(`${wsId}|${from}|${to}`, { groupId: wsId, from, to });
    };
    ids.forEach((id) => addWin(id, boardWinForRates, custom));
    if (hasMetricPlan(plan)) {
      const perWsForRates = ids.map((id) => ({
        id,
        defs: liveKpisOf((companyKpis && companyKpis[id]) || []),
        defsAll: ((companyKpis && companyKpis[id]) || []).filter((k) => k && !k.isDraft && !k.disabled && nameKey(k)),
      }));
      const columnsForRates = effectiveMetricColumns(perWsForRates, plan, true);
      columnsForRates.forEach((c) => {
        ids.forEach((id) => {
          const sw = sourceWindowOf(c, id, boardWinForRates, custom);
          const srcWin = sw.window === "year" ? "ytd" : sw.window;
          addWin(id, srcWin, sw.custom);
        });
      });
    }
    if (!needed.size) {
      setWindowRatesByWs({});
      return undefined;
    }
    let alive = true;
    Promise.all(
      [...needed.values()].map(async ({ groupId, from, to }) => {
        const rates = await fetchWindowRates({ groupId, from, to });
        return [`${groupId}|${from}|${to}`, rates];
      }),
    ).then((pairs) => {
      if (!alive) return;
      setWindowRatesByWs(Object.fromEntries(pairs));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, ids, ratesKey]);

  return useMemo(() => {
    const { from, to } = resolveWindow(bucket, custom);
    /* Recomputed here (not reused from the fetch effect above) so the key
       is guaranteed to match what was actually fetched under — resolveWindow
       and boundsForWindow don't return the same value shape. */
    const ratesFor = (wsId, w, cust) => {
      const bounds = boundsForWindow(w, cust);
      if (!bounds.from || !bounds.to) return null;
      return windowRatesByWs[`${wsId}|${bounds.from}|${bounds.to}`] || null;
    };

    /* The live metric list of each workspace, in the workspace's own order. */
    const perWs = ids.map((id) => ({
      id,
      defs: liveKpisOf((companyKpis && companyKpis[id]) || []),
      defsAll: ((companyKpis && companyKpis[id]) || []).filter((k) => k && !k.isDraft && !k.disabled && nameKey(k)),
    }));

    /* Columns: the admin's metric plan when one is set (differently-named
       metrics paired per workspace, summed or averaged per column), otherwise
       the auto name-match — what they all track. Each column names, per
       workspace, which metric id feeds it. */
    const columns = effectiveMetricColumns(perWs, plan, true);

    /* Real roster name — orphan score keys / blank profiles render as "Player". */
    const displayNameOf = (p) => {
      if (!p) return "";
      const full = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
      if (full) return full;
      return String(p.name || p.email || "").trim();
    };
    const isBoardPlayer = (p) => {
      if (!p) return false;
      if (p.isArchived || p.isActive === false || p.lastName === "Yeti" || p.isAdmin || p.isYeti) {
        return false;
      }
      const label = displayNameOf(p);
      if (!label || /^player$/i.test(label)) return false;
      return true;
    };

    /* Seed EVERY active player in the scoped workspaces, then overlay scores.
       Building only from scorers hid people with 0 for the day/period. */
    const byUser = {};
    const cellsByUser = {};
    const idSet = new Set(ids);
    (allUsers || []).forEach((u) => {
      if (!u) return;
      const gid = String(u.groupId || "");
      if (!idSet.has(gid)) return;
      const userId = String(u.userId || u._id || "");
      if (!userId) return;
      const merged = { ...(usersDict?.[userId] || {}), ...u, userId };
      if (!isBoardPlayer(merged)) return;
      if (!byUser[userId]) {
        byUser[userId] = {
          userId,
          pts: 0,
          values: {},
          wsId: gid,
          ...merged,
        };
        cellsByUser[userId] = {};
      }
    });

    perWs.forEach((w) => {
      const metaById = metaByIdFromDefs(w.defsAll);
      const { perUser } = sumScores(scoresByWs[w.id], from, metaById, to);
      if (bucket === "lastMonth") {
        // Score Last Month against the workspace's own enabled KPI set (defsAll)
        // — same set the live board uses.
        applyClosedMonthRecompute(perUser, w.defsAll);
      } else {
        const rates = ratesFor(w.id, boardWinForRates, custom);
        if (rates) applyWindowRatesToPerUser(perUser, rates);
      }
      Object.entries(perUser).forEach(([userId, v]) => {
        const profile = usersDict?.[userId] || {};
        if (!isBoardPlayer({ ...profile, userId })) return;
        if (!byUser[userId]) {
          /* Scorer not in roster dump (edge) — still show them if named. */
          byUser[userId] = { userId, pts: 0, values: {}, wsId: w.id, ...profile };
          cellsByUser[userId] = {};
        }
        /* Same recompute as classic This Month / LiveLeaderboard — server-stored
           pts drift (e.g. Estimate Won Value 109k vs rule → ~2k). Raw sumScores
           totals made custom ranges show six-digit ranks while presets stayed
           honest. */
        const shaped = { total: 0 };
        Object.entries(v.kpis || {}).forEach(([id, cell]) => {
          shaped[id] = {
            amt: Number(cell?.amt) || 0,
            pts: Number(cell?.pts) || 0,
          };
        });
        /* lastMonth: applyClosedMonthRecompute already wrote pts onto v.kpis /
           v.pts. Rebuilding `shaped` with total:0 then reading shaped.total
           (0 ?? v.total is still 0) zeroed every player while Yeti stayed live. */
        let recomputed;
        if (bucket === "lastMonth") {
          const closed = v.kpis && typeof v.kpis === "object" ? v.kpis : shaped;
          const closedTotal = Number(v.pts ?? closed.total) || 0;
          recomputed = { ...closed, total: closedTotal };
        } else {
          recomputed = recomputeScopedPlayerScores(shaped, w.defsAll);
        }
        const rowPts = Number(recomputed.total) || 0;
        byUser[userId].pts += Math.round(rowPts * 100) / 100;
        byUser[userId].scores = recomputed;
      });
      columns.forEach((c) => {
        const kpiId = c.sources?.[String(w.id)];
        if (!kpiId) return;
        /* A saved Custom plan can point a column at a metric that has since
           been marked scorekeeper-only — that source must go dead (like a
           retired metric), not resurrect the metric onto this board. */
        const srcDef = w.defsAll.find((x) => String(x._id) === String(kpiId)) || null;
        if (isKpiScorekeeperOnly(srcDef)) return;
        const boardWin = bucket === "year" ? "ytd" : bucket;
        const sw = sourceWindowOf(c, w.id, boardWin, custom);
        const srcWin = sw.window === "year" ? "ytd" : sw.window;
        const { from: srcFrom, to: srcTo } = resolveWindow(srcWin, sw.custom);
        let colUsers;
        if (bucket === "lastMonth") {
          colUsers = perUser;
        } else {
          const colResult = sumScores(scoresByWs[w.id], srcFrom, metaById, srcTo);
          colUsers = colResult.perUser;
          const colRates = ratesFor(w.id, srcWin, sw.custom);
          if (colRates) applyWindowRatesToPerUser(colUsers, colRates);
        }
        Object.entries(colUsers).forEach(([userId, v]) => {
          const profile = usersDict?.[userId] || {};
          if (!isBoardPlayer({ ...profile, userId })) return;
          if (!byUser[userId]) {
            byUser[userId] = { userId, pts: 0, values: {}, wsId: w.id, ...profile };
            cellsByUser[userId] = {};
          }
          const rawCell = v.kpis?.[kpiId];
          const def = w.defsAll.find((x) => String(x._id) === String(kpiId))
            || w.defs.find((x) => String(x._id) === String(kpiId))
            || null;
          const amt = effectiveMetricAmt(def, rawCell, { missing: !rawCell });
          const scored = metricCellScored(def, rawCell, { missing: !rawCell });
          const pts = bucket === "lastMonth"
            ? (Number(rawCell?.pts) || recomputeKpiCellPts(rawCell, def))
            : recomputeEntryPts(rawCell, def);
          (cellsByUser[userId][c.key] || (cellsByUser[userId][c.key] = [])).push({
            amt,
            pts,
            present: scored,
          });
        });
      });
    });
    Object.entries(byUser).forEach(([userId, row]) => {
      row.metricPts = {};
      columns.forEach((c) => {
        let cells = cellsByUser[userId][c.key] || [];
        if (!cells.length) {
          const kpiId = c.sources?.[String(row.wsId)];
          const ws = perWs.find((x) => String(x.id) === String(row.wsId));
          const def = kpiId && ws
            ? (ws.defsAll.find((x) => String(x._id) === String(kpiId))
              || ws.defs.find((x) => String(x._id) === String(kpiId))
              || null)
            : null;
          if (isBidAccuracyKpi(def)) {
            cells = [{ amt: 1, pts: 0, present: true }];
          }
        }
        row.values[c.key] = poolColumn(cells, c.mode);
        row.metricPts[c.key] = poolPts(cells, c.mode);
      });
    });

    const rows = Object.values(byUser).filter(isBoardPlayer);

    /* Where everyone stood last period, so Movement is a real comparison.
       Nobody last period = new, which reads as "new", not as a fall to last. */
    const prev = previousWindow(bucket);
    const prevPts = {};
    if (prev.from && prev.to) {
      perWs.forEach((w) => {
        Object.entries(pointsBetween(scoresByWs[w.id], prev.from, prev.to)).forEach(([id, pts]) => {
          prevPts[id] = (prevPts[id] || 0) + pts;
        });
      });
    }
    const prevOrder = Object.keys(prevPts).sort((a, b) => prevPts[b] - prevPts[a]);
    rows.forEach((r) => {
      const i = prevOrder.indexOf(String(r.userId));
      r.wasRank = i < 0 ? null : i + 1;
    });

    /* The Yeti's pace over the same [from, to] as player scores.
       Named presets (today/week/…) map 1:1. Custom / quarter / YTD / year use
       the range-paced `custom` bucket — never bare month / MTD. */
    let yetiMonthRef = companyMonthStr();
    if (bucket === "lastMonth") yetiMonthRef = lastMonthKey();
    else if (windowUsesRangeYeti(bucket) && from?.isValid?.()) {
      yetiMonthRef = from.format("YYYY-MM");
    }
    let yetiAsOf = companyToday();
    if (bucket === "lastMonth") {
      yetiAsOf = moment(yetiMonthRef, "YYYY-MM").endOf("month");
    } else if (windowUsesRangeYeti(bucket) && to?.isValid?.()) {
      yetiAsOf = moment.min(to.clone().endOf("day"), companyToday().endOf("day"));
    }
    const yetiRange = windowUsesRangeYeti(bucket) && from?.isValid?.()
      ? {
        from: from.format("YYYY-MM-DD"),
        to: (to?.isValid?.() ? to : from).format("YYYY-MM-DD"),
      }
      : null;
    const yetiBucket = yetiBucketForWindow(bucket === "year" ? "ytd" : bucket);
    const yetiByWs = {};
    perWs.forEach((w) => {
      yetiByWs[w.id] = w.defs.length
        ? buildYetiScoresForKpis(w.defs, yetiMonthRef, holidays, yetiAsOf, yetiRange)
        : null;
    });

    /* Per-metric beat flags for the qualify gate (e.g. 4 of 5 must be green).
       Bar = max(Yeti period amount, Scorekeeper "of X" for this window) so
       AVG Ticket $850 cannot green against a $1,250 card target. */
    rows.forEach((r) => {
      const y = yetiByWs[String(r.wsId)];
      const bucketScore = y?.[yetiBucket] || {};
      r.yetiPts = Number(bucketScore.total) || 0;
      const beats = {};
      let beatCount = 0;
      columns.forEach((c) => {
        const kpiId = c.sources?.[String(r.wsId)];
        const ws = perWs.find((x) => String(x.id) === String(r.wsId));
        const def = kpiId && ws
          ? (ws.defsAll.find((x) => String(x._id) === String(kpiId))
            || ws.defs.find((x) => String(x._id) === String(kpiId))
            || null)
          : null;
        const kind = kindOfKpi(def, c.kind);
        const yetiCell = kpiId ? bucketScore[kpiId] : null;
        const yetiAmt = yetiCell && Number.isFinite(Number(yetiCell.amt)) ? Number(yetiCell.amt) : 0;
        const scorekeeperBar = def ? displayTargetOf(def, kind, bucket, custom) : 0;
        const bar = Math.max(yetiAmt, Number(scorekeeperBar) || 0);
        const yetiMetricPts = yetiCell && Number.isFinite(Number(yetiCell.pts)) ? Number(yetiCell.pts) : 0;
        const playerMetricPts = Number(r.metricPts?.[c.key]) || 0;
        const playerAmtRaw = Number(r.values?.[c.key]) || 0;
        const playerAmt = effectiveMetricAmt(def, { amt: playerAmtRaw, scored: playerAmtRaw > 0 }, { missing: playerAmtRaw === 0 && isBidAccuracyKpi(def) });
        const scored = metricCellScored(def, { amt: playerAmt, scored: playerAmt > 0 }, { missing: playerAmtRaw === 0 });
        const ok = (yetiCell != null || scorekeeperBar > 0) && beatsYetiMetric(
          playerMetricPts,
          yetiMetricPts,
          playerAmt,
          bar,
          kind,
          scored,
        );
        beats[c.key] = ok;
        if (ok) beatCount += 1;
      });
      r.beats = beats;
      r.beatCount = beatCount;
      r.metricCount = columns.length;
    });

    /* Board-level Yeti: one workspace → that pace; several → average of each
       workspace's Yeti (same rule as useScopedMetrics team/yeti on "All"). */
    let yetiPeriod = null;
    let yetiPts = 0;
    if (ids.length === 1) {
      yetiPeriod = yetiByWs[ids[0]]?.[yetiBucket] || null;
      yetiPts = Number(yetiPeriod?.total) || 0;
    } else if (ids.length > 1) {
      const totals = ids
        .map((id) => Number(yetiByWs[id]?.[yetiBucket]?.total) || 0)
        .filter((n) => Number.isFinite(n));
      yetiPts = totals.length
        ? totals.reduce((a, b) => a + b, 0) / totals.length
        : 0;
      yetiPeriod = { total: yetiPts };
    }

    return { columns, rows, yetiPts, yetiScores: yetiPeriod, loading, refreshing };
    // `plan` must be a dep — Setup edits to metricPlan were ignored and the
    // table kept the old auto columns (Memberships / YTD) forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, companyKpis, scoresByWs, windowRatesByWs, bucket, customKey, holidays, usersDict, allUsers, loading, refreshing, JSON.stringify(plan), historicRows]);
}
